/**
 * CtaButtonBlock: `cta-button` content block.
 *
 * data: { text, link, variant? }
 *
 * `variant` is a Bootstrap button variant (default 'primary'). The `link` may
 * be an internal route (relative, e.g. `/directory`) or an external URL
 * (absolute `http(s)://`). Internal links route through React Router's
 * `<Link>` so the tenant basePath is applied; external links render as a
 * plain anchor opening in a new tab.
 *
 * Degrades gracefully: a missing `text` or `link` renders nothing.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';

import {
    isAllowedLinkUrl,
    isRawAnchorUrl,
    isExternalHttpUrl,
} from '../utils/urlValidator';

// Bootstrap button variants accepted from authored content.
// Must stay in sync with CTA_BUTTON_VARIANTS (editor/blockTypes.js) and
// ALLOWED_VARIANTS in EcosystemJoinBlock.jsx. A direct import is not possible
// here because blockTypes.js imports blockDefinitions.js, which imports this
// file — any import in this direction would create a circular dependency.
const ALLOWED_VARIANTS = [
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'light',
    'dark',
    'accent',
    'link',
    'outline-primary',
    'outline-secondary',
    'outline-success',
    'outline-danger',
    'outline-warning',
    'outline-info',
    'outline-light',
    'outline-dark',
];

export default function CtaButtonBlock({ data }) {
    const { text, link, variant } = data || {};

    if (!text || !link) {
        return null;
    }

    // Reject `javascript:`, `data:`, `vbscript:`, etc. URL schemes, even
    // if a pre-validation row exists in the DB from before the server-side
    // allowlist landed, the renderer defangs it (round-2 review finding 3).
    // The block renders nothing rather than handing the URL to <Link>.
    if (!isAllowedLinkUrl(link)) {
        return null;
    }

    // Unknown / missing variant falls back to 'primary'.
    const safeVariant = ALLOWED_VARIANTS.includes(variant)
        ? variant
        : 'primary';

    // Round-4 review finding 8: `mailto:` (and `tel:`) belong on a plain
    // `<a href>`, not inside React Router's <Link>. The prior code only
    // recognized `https?://` as external; mailto: fell into the else branch
    // and got handed to <Link>, which routed it as a relative SPA path and
    // landed users on the catch-all 404 page instead of opening the mail
    // client. isRawAnchorUrl covers http(s) + mailto + tel; only http(s) get
    // the new-tab target attributes (the OS handles mailto/tel).
    // size="lg" + generous padding + top margin so the CTA reads as a
    // deliberate, prominent action rather than a small orphaned button. It
    // aligns left within the content column like the rest of the body.
    return isRawAnchorUrl(link) ? (
        <Button
            href={link}
            {...(isExternalHttpUrl(link) && {
                target: '_blank',
                rel: 'noopener noreferrer',
            })}
            variant={safeVariant}
            size="lg"
            className="rounded-pill px-4 mt-2"
        >
            {text}
        </Button>
    ) : (
        <Button
            as={Link}
            to={link}
            variant={safeVariant}
            size="lg"
            className="rounded-pill px-4 mt-2"
        >
            {text}
        </Button>
    );
}

CtaButtonBlock.propTypes = {
    data: PropTypes.shape({
        text: PropTypes.string,
        link: PropTypes.string,
        variant: PropTypes.string,
    }),
};

CtaButtonBlock.defaultProps = {
    data: {},
};
